<template>
  <section
    class="spotlight"
    v-if="isActive"
    :data-e2e="SpotlightCypressAttribute.views.SEARCH_MODAL"
  >
    <div class="overlay" @click.stop="close"></div>
    <div class="inner is-flex">
      <section class="search-panel">
        <FormInput
          type="text"
          ref="refInput"
          v-model="searchQuery"
          :placeholder="$t('Search for anything...')"
          :show-float-label="false"
          @input="onInput"
          :data-e2e="SpotlightCypressAttribute.inputs.SEARCH"
        />
        <SpotlightSearchInputTip
          v-if="showKeyboardShortcutInfo"
          suffix="to show this search"
          command="⌘+K"
        />
        <div v-if="isSearching" class="is-full-absolute xy-t-90">
          <LoadingView :active="true" :is-full-page="false" />
        </div>
        <TabsView
          v-model="tabValue"
          :animated="false"
          ref="tabs"
          @input="onTabChange"
          :destroy-on-hide="true"
        >
          <SpotlightSearchHeader
            v-if="groupsToDisplay.hasItems"
            :total="groupsToDisplay.total"
            :is-from-search="groups.hasItems"
          />
          <div v-else class="is-flex-centered is-flex-direction-column">
            <img src="@/assets/illustrations/search-empty.svg" alt="" class="m-t-50" />
            <p class="title is-size-4 m-t-25">{{ $t('No results found') }}</p>
            <p class="subtitle is-size-6">{{ $t('Try using different keywords.') }}</p>
          </div>
          <TabItemView :label="$t('All')" :value="ModelType.GENERIC">
            <template #body>
              <SpotlightGroups
                :groups="groupsToDisplay"
                :show-type-title="true"
                @on-item-click="onItemClick"
              />
            </template>
          </TabItemView>
          <TabItemView :label="$t('Estates')" :value="ModelType.ESTATE">
            <template #body>
              <SpotlightGroups :groups="estateGroup" @on-item-click="onItemClick" />
            </template>
          </TabItemView>
          <TabItemView :label="$t('Documents')" :value="ModelType.DOCUMENT">
            <template #body>
              <SpotlightGroups :groups="documentGroup" @on-item-click="onItemClick" />
            </template>
          </TabItemView>
          <TabItemView :label="$t('Assets')" :value="ModelType.ASSET">
            <template #body>
              <SpotlightGroups :groups="assetGroup" @on-item-click="onItemClick" />
            </template>
          </TabItemView>
          <TabItemView :label="$t('Liabilities')" :value="ModelType.LIABILITY">
            <template #body>
              <SpotlightGroups :groups="liabilityGroup" @on-item-click="onItemClick" />
            </template>
          </TabItemView>
        </TabsView>
      </section>
      <SpotlightSearchFilters @on-filter="onFilter" />
    </div>
  </section>
</template>

<script>
import KeyboardAdapter from '@/modules/core/ui/services/KeyboardService';
import { KeyboardEvent } from '@/modules/core/ui/ui.constants';
import { debounce } from 'lodash';
import SpotlightService from '@/modules/spotlight/services/SpotlightService';
import { SpotlightGroupCollection } from '@/modules/spotlight/models/SpotlightGroupCollection';
import { SpotlightSearchTab } from '@/modules/spotlight/constants/SpotlightSearchTab';
import { HasAuthUserMixin } from '@/modules/core/app/mixins/HasAuthUserMixin';
import SpotlightSearchFilters from '@/modules/spotlight/components/stateful/SpotlightSearchFilters.vue';
import { SpotlightSearchFilterValues } from '@/modules/spotlight/constants/SpotlightSearchFilterValues';
import SpotlightGroups from '@/modules/spotlight/components/presentational/SpotlightGroups.vue';
import { SpotlightSearchParams } from '@/modules/spotlight/models/SpotlightSearchParams';
import { SpotlightSearchFilter } from '@/modules/spotlight/constants/SpotlightSearchFilter';
import { SpotlightEvent } from '@/modules/spotlight/events/SpotlightEvent';
import { ModelType } from '@/modules/core/app/constants/ModelType';
import { SpotlightCypressAttribute } from '@/modules/spotlight/constants/SpotlightCypressAttribute';
import FormInput from '@/modules/core/form/components/FormInput.vue';
import SpotlightRecentlyViewedCache from '@/modules/spotlight/caches/SpotlightRecentlyViewedCache';
import SpotlightSearchHeader from '@/modules/spotlight/components/stateful/SpotlightSearchHeader.vue';
import SpotlightSearchInputTip from '@/modules/spotlight/components/functional/SpotlightSearchInputTip.vue';
import TabsView from '@/modules/core/ui/components/wrappers/TabsView.vue';
import TabItemView from '@/modules/core/ui/components/wrappers/TabItemView.vue';
import LoadingView from '@/modules/core/ui/components/wrappers/LoadingView.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'SpotlightSearch',
  mixins: [HasAuthUserMixin],
  components: {
    TabItemView,
    TabsView,
    SpotlightSearchInputTip,
    FormInput,
    SpotlightSearchHeader,
    SpotlightGroups,
    SpotlightSearchFilters,
    LoadingView
  },
  static: {
    ModelType,
    tab: SpotlightSearchTab,
    SpotlightCypressAttribute
  },
  local() {
    return {
      minSearchLength: 2
    };
  },
  events() {
    return {
      [SpotlightEvent.TRIGGER_SEARCH]: this.toggleActiveWithInfo,
      [SpotlightEvent.CLOSE_SEARCH]: this.close
    };
  },
  data() {
    return {
      groups: SpotlightGroupCollection.mapModel([]),
      params: new SpotlightSearchParams(),
      filters: SpotlightSearchFilterValues,
      isActive: false,
      isSearching: false,
      searchQuery: null,
      debounceQuery: null,
      showKeyboardShortcutInfo: false,
      tabValue: ModelType.GENERIC
    };
  },
  computed: {
    canSearch() {
      return !!this.searchQuery && this.searchQuery.length >= this.minSearchLength;
    },
    groupsToDisplay() {
      if (!this.groups.hasItems) {
        const recentlyViewedGroups = SpotlightRecentlyViewedCache.getItems();
        if (recentlyViewedGroups.hasItems) {
          return recentlyViewedGroups;
        }
      }
      return this.groups;
    },
    estateGroup() {
      return this.getGroup(this.groupsToDisplay.estateGroup);
    },
    assetGroup() {
      return this.getGroup(this.groupsToDisplay.assetGroup);
    },
    documentGroup() {
      return this.getGroup(this.groupsToDisplay.documentGroup);
    },
    liabilityGroup() {
      return this.getGroup(this.groupsToDisplay.liabilityGroup);
    }
  },
  methods: {
    onInput(value) {
      if (value !== this.computedValue) {
        this.debounceQuery(value);
      }
    },
    toggleActive() {
      if (this.authUser.isViewingEstateDetails) {
        return;
      }
      this.isActive = !this.isActive;
      if (this.isActive) {
        this.$nextTick(() => this.$refs.refInput.focus());
      }
    },
    toggleActiveWithoutInfo() {
      this.showKeyboardShortcutInfo = false;
      this.toggleActive();
    },
    toggleActiveWithInfo() {
      this.showKeyboardShortcutInfo = true;
      this.toggleActive();
    },
    onTabChange() {
      if (this.tabValue === ModelType.GENERIC) {
        this.params.setLimit(5);
        this.params.setModelType(null);
      } else {
        this.params.setLimit(25);
        this.params.setModelType(this.tabValue);
      }
      this.search();
    },
    onFilter(filters) {
      this.filters = filters;
      this.search();
    },
    async search() {
      this.resetGroups();

      if (this.canSearch) {
        this.isSearching = true;
        this.setSearchParams();
        this.groups = await this.fetchData();
        this.isSearching = false;
      }
    },
    setSearchParams() {
      this.params.setSearchQuery(this.searchQuery);
      this.params.setUserHasEstateAccount(
        this.filters[SpotlightSearchFilter.ESTATE_ASSIGNED_TO_ME]
      );
      this.params.setOnlyClosed(this.filters[SpotlightSearchFilter.SHOW_CLOSED]);
      this.params.setOnlyTrashed(this.filters[SpotlightSearchFilter.SHOW_DELETED]);
    },
    async fetchData() {
      return SpotlightService.search(this.params);
    },
    onEscape() {
      if (this.isActive && !!this.searchQuery) {
        this.resetSearch();
      } else {
        this.close();
      }
    },
    onItemClick(item) {
      SpotlightRecentlyViewedCache.addItem(item);
      this.close();
    },
    getGroup(group) {
      return SpotlightGroupCollection.mapModel([group]);
    },
    resetGroups() {
      this.groups = SpotlightGroupCollection.mapModel([]);
    },
    resetSearch() {
      this.resetGroups();
      this.searchQuery = null;
      this.params = new SpotlightSearchParams();
      this.filters = SpotlightSearchFilterValues;
    },
    close() {
      this.resetSearch();
      this.isActive = false;
      this.tabValue = ModelType.GENERIC;
      this.showKeyboardShortcutInfo = false;
    }
  },
  created() {
    this.debounceQuery = debounce(this.search, 500);
    KeyboardAdapter.on(KeyboardEvent.CMD_K, this.toggleActiveWithoutInfo);
    KeyboardAdapter.on(KeyboardEvent.ESCAPE, this.onEscape);
  },
  beforeUnmount() {
    KeyboardAdapter.off(KeyboardEvent.CMD_K, this.toggleActiveWithoutInfo);
    KeyboardAdapter.off(KeyboardEvent.ESCAPE, this.onEscape);
  }
};
</script>

<style scoped>
section.spotlight div.inner .search-panel :deep(> section.form-input) {
  margin-bottom: var(--ce-20px) !important;
}

section.spotlight div.inner .search-panel :deep(input) {
  padding: var(--ce-30px) var(--ce-20px) var(--ce-25px) var(--ce-20px);
  font-size: var(--ce-18px);
  border: 0;
}

section.spotlight div.inner .search-panel :deep(input:focus) {
  box-shadow: none;
}

section.spotlight div.inner :deep(.loading-overlay .loading-background) {
  background: var(--ce-white);
}

section.spotlight div.inner :deep(.b-tabs .tabs) {
  font-size: var(--ce-12px);
  margin-bottom: 0;
}

section.spotlight div.inner :deep(.b-tabs .tabs ul) {
  padding-left: var(--ce-12px);
  border-bottom-width: 1px;
}

section.spotlight div.inner :deep(.b-tabs .tab-content) {
  padding: 0;
  position: initial;
}

section.spotlight {
  display: flex;
  z-index: var(--ce-z-index-7);
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: transparent;
  justify-content: center;
  align-items: center;

  div.inner {
    box-shadow: 0 var(--ce-4px) var(--ce-16px) rgba(0, 0, 0, 0.08);
    border-radius: var(--ce-radius-large);
    opacity: 0;
    animation-name: slide-fade-in-bu;
    animation-duration: 0.25s;
    animation-delay: 0;
    animation-fill-mode: forwards;
    transform: translateY(10px);
    transition: opacity 0.5s ease, transform 0.5s ease;

    position: relative;
    z-index: var(--ce-z-index-8);
    background: var(--ce-white);
    width: var(--ce-1000px);
    height: 70%;
    margin-top: var(--ce--40px);

    .search-panel {
      flex: 1;
      position: relative;
    }
  }
}
</style>
